import moment from 'moment';

import DatePicker from '../../../../common/DatePicker';
import { RadioGroupOption, RadioGroupOptionPassdownProps } from '../../../../common/RadioGroup/RadioGroup';
import getSessionsWithin48Hours from '../../../../common/helpers/getSessionsWithin48Hours';
import pluralize from '../../../../common/helpers/pluralize';
import { AffectedSessions } from '../../../../legacy/components';
import { SessionExcludedAlert } from '../../../../legacy/components';
import { Dropdown } from '../../../../legacy/components';
import { PauseEnrolmentContextType } from '../../PauseEnrolmentContext';
import { isSummerPausePromoEnabled } from '../../helpers';
import ValidationErrors from './ValidationErrors';
import {
  enrolmentIsPaused,
  enrolmentWillBePaused,
  formatHolidayDisplayText,
  formatSchoolTermEndDateText,
} from './helpers';
import { PauseFormValidationSchemaType } from './validation';

export const createPauseByWeeksOption = (context: PauseEnrolmentContextType): RadioGroupOption => {
  const {
    pauseByWeeksRange,
    pauseByWeeksValue,
    setPauseByWeeksValue,
    pauseEndDate,
    affectedSessions,
    timezone,
    sessions,
    selectedEnrolment,
    validationErrors,
  } = context;
  const { enrolmentId } = selectedEnrolment;
  const errors =
    validationErrors?.pauseStartDate?.['pause-by-weeks']?._errors || validationErrors?.resumptionDate?._errors || null;
  return {
    value: 'pause-by-weeks',
    renderOption: (props: RadioGroupOptionPassdownProps) => {
      const { checked, id } = props;
      return (
        <div>
          <div className="flex items-center space-x-4">
            <div>Next</div>
            <Dropdown
              id={id}
              options={pauseByWeeksRange}
              selectedOption={pauseByWeeksValue}
              setSelectedOption={setPauseByWeeksValue}
              disabled={!checked}
            />
            <div>{pluralize('week', pauseByWeeksValue, 'weeks')}</div>
          </div>
          <ValidationErrors<PauseFormValidationSchemaType>
            fieldName="resumptionDate"
            checked={checked}
            errors={errors}
          />
        </div>
      );
    },
    renderBelowOption: (props: RadioGroupOptionPassdownProps) => {
      if (!props.checked) {
        return null;
      }

      return (
        <div className="ml-[3rem]">
          <div className="my-4">
            {getSessionsWithin48Hours({ enrolmentId, sessions, timezone }).map((s) => (
              <SessionExcludedAlert session={s} timezone={timezone} />
            ))}
          </div>
          {!!pauseEndDate && (
            <AffectedSessions sessions={affectedSessions} timezone={timezone} resumeFromDate={pauseEndDate} />
          )}
        </div>
      );
    },
  };
};

export const createPauseDuringHolidaysOption = (context: PauseEnrolmentContextType): RadioGroupOption => {
  const { currentOrUpcomingHoliday, currentSchoolTerm, pauseStartDate, pauseEndDate } = context;
  return {
    value: 'pause-during-holidays',
    renderOption: () => {
      return <div>{formatHolidayDisplayText(currentOrUpcomingHoliday)}</div>;
    },
    renderBelowOption: (props: RadioGroupOptionPassdownProps) => {
      if (currentSchoolTerm) {
        return (
          <div>
            <div className="mt-0 flex flex-row">
              <div className="w-[3rem]"></div>
              <label htmlFor={props.id} className="cursor-pointer text-xs text-custom-4">
                {formatSchoolTermEndDateText(currentSchoolTerm)}
              </label>
            </div>
            {props.checked && (
              <div className="ml-[3rem] mt-6 min-w-[320px] border-l-2 border-grey-2 pl-5 md:min-w-[350px]">
                <div className="mb-5">
                  <p className="ls-1px mb-3 text-xs font-bold uppercase">PAUSE WILL COMMENCE ON</p>
                  <ul className="text-sm leading-snug">{moment(pauseStartDate).format('dddd D MMMM YYYY')}</ul>
                </div>
                <div>
                  <p className="ls-1px mb-3 text-xs font-bold uppercase">SESSIONS WILL RESUME FROM</p>
                  <ul className="fs-15px list-unstyled leading-snug">
                    {moment(pauseEndDate).format('dddd D MMMM YYYY')}
                  </ul>
                </div>
              </div>
            )}
          </div>
        );
      }
      return null;
    },
  };
};

export const createPauseBySpecificDateOption = (context: PauseEnrolmentContextType): RadioGroupOption => {
  const {
    pauseMinDate,
    pauseMaxDate,
    specificPauseStartDate,
    setSpecificPauseStartDate,
    validationErrors,
    selectedEnrolment,
  } = context;
  const isPaused = enrolmentIsPaused(selectedEnrolment);
  const errors =
    validationErrors?.pauseStartDate?.['pause-by-specific-date']?._errors ||
    validationErrors?.pauseStartDate?._errors ||
    null;

  return {
    value: 'pause-by-specific-date',
    renderOption: () => {
      return <div>Pause from:</div>;
    },
    renderBelowOption: (props: RadioGroupOptionPassdownProps) => {
      const { checked, id } = props;
      const tomorrow = moment().startOf('day').add(1, 'day');

      return (
        <div className="ml-[3rem] max-w-[320px]">
          <DatePicker
            key={`${id}_checked_${checked}`} // need this dynamic key to force the native input to re-render
            placeholder="Select start date"
            id={id}
            disabled={!checked || isPaused}
            options={{
              mode: 'single',
              minDate: moment.max([pauseMinDate, tomorrow]).utc().format(),
              maxDate: pauseMaxDate.utc().format(),
            }}
            value={specificPauseStartDate}
            onChange={(date: Date[]) => {
              setSpecificPauseStartDate(date[0].toISOString());
            }}
          />
          <ValidationErrors<PauseFormValidationSchemaType>
            fieldName="pauseStartDate"
            checked={checked}
            errors={errors}
          />
        </div>
      );
    },
  };
};

export const createPauseMethods = (context: PauseEnrolmentContextType): RadioGroupOption[] => {
  const { sessionPolicy, currentOrUpcomingHoliday, pauseMinDate, pauseMaxDate, selectedEnrolment } = context;
  const isFlexi = sessionPolicy?.pause?.pauseAllowed === 'Anytime';
  const isPausingForSummerHoliday = currentOrUpcomingHoliday?.type === 'Summer Holiday';
  const isWithinPauseWindow = moment().isBetween(pauseMinDate, pauseMaxDate, 'day', '[]');

  if (enrolmentWillBePaused(selectedEnrolment) || enrolmentIsPaused(selectedEnrolment)) {
    return [createPauseBySpecificDateOption(context)];
  }

  if (isFlexi && isPausingForSummerHoliday && (isWithinPauseWindow || isSummerPausePromoEnabled())) {
    return [createPauseDuringHolidaysOption(context), createPauseBySpecificDateOption(context)];
  }
  if (isFlexi) {
    return [createPauseByWeeksOption(context)];
  }

  if (isPausingForSummerHoliday && (isWithinPauseWindow || isSummerPausePromoEnabled())) {
    return [createPauseBySpecificDateOption(context)];
  }

  return [];
};
